import {Key, useEffect, useState} from 'react';
import {Box, Chip, InputAdornment, Typography} from '@mui/material';
import TextField from '@jobseeker/lotus/elements/Input';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  SearchIcon,
  WhiteCrossOutlined,
  CheckIcon,
  UnCheckIcon,
  OrangeCheckIcon,
  CheckedOrange,
} from '@jobseeker/lotus/icons';
import {borderRadius} from '@mui/system';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

interface MobileSelectDialogV2Props {
  open: boolean;
  onClose: () => void;
  title: string;
  options: {label: string; value: string | number}[];
  onAdd: any;
  onDelete: any;
  selectedValues: string[];
  subtitle?: string;
  label?: string;
  maxLimit: number;
}

const Checkbox = ({checked}: any) => {
  return (
    <Image
      src={checked ? (isAppIimjobs ? CheckIcon : CheckedOrange) : UnCheckIcon}
      alt="checkbox"
      width={16}
      height={16}
    />
  );
};

const MobileSelectDialogV2: React.FC<MobileSelectDialogV2Props> = ({
  open,
  onClose,
  title,
  options,
  onAdd,
  onDelete,
  selectedValues,
  subtitle,
  label,
  maxLimit,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showError, setShowError] = useState(false);
  const handleSelection = (value: string | number) => {
    const stringValue = String(value);
    let newValues = Array.isArray(selectedValues) ? selectedValues : [];

    if (newValues.includes(stringValue)) {
      newValues = newValues.filter((v: string) => v !== stringValue);
      setShowError(false);
    } else {
      if (newValues.length < maxLimit) {
        newValues.push(stringValue);
      } else {
        setShowError(true);
      }
    }

    onAdd(newValues);
  };
  useEffect(() => {
    if (selectedValues.length > maxLimit) {
      setShowError(true);
    } else setShowError(false);
  }, [selectedValues]);

  const deleteChip = (chipValue: string) => {
    onDelete(chipValue);
  };

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      invertedTitle={false}
      variant={VersionVariants.VERSION_EIGHT}
      onDoneClick={() => {
        setSearchQuery('');
        setShowError(false);
      }}
      subtitle={subtitle}
    >
      <Box>
        <TextField
          fullWidth
          placeholder={`${label}`}
          value={searchQuery}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Image src={SearchIcon} alt="search" width={16} height={16} />
              </InputAdornment>
            ),
          }}
          sx={{
            '.MuiInputBase-input': {
              border: 'unset !important',
              fontSize: '12px !important',
              paddingLeft: 0,
              py: '13px',
            },
            '.MuiInputBase-input::placeholder': {
              fontSize: '12px',
              fontWeight: 400,
              color: '#7E7E7E',
              opacity: 0.8,
            },
            mb: '8px',
          }}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'Escape') e.stopPropagation();
          }}
          variant={VersionVariants.VERSION_TWO}
        />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mb: 1,
            overflowY: 'auto',
          }}
        >
          {selectedValues.map((chipValue: string) => {
            const selectedOption = options.find(
              option => String(option.value) === chipValue
            );
            return (
              <Chip
                key={chipValue}
                label={
                  <Typography
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.875}
                    variant="body2"
                    lineHeight="18px"
                  >
                    {selectedOption?.label ?? chipValue}{' '}
                    <Image
                      src={WhiteCrossOutlined}
                      width={12}
                      height={12}
                      alt=""
                      style={{cursor: 'pointer'}}
                      onClick={() => deleteChip(chipValue)}
                    />
                  </Typography>
                }
                sx={{
                  height: 30,
                  backgroundColor: !isAppIimjobs ? '#E9630C' : '#333',
                }}
                variant="filled"
                color="secondary"
              />
            );
          })}
        </Box>
        {showError && (
          <Typography variant="caption" color="error" lineHeight={'14px'}>
            {`You can select maximum ${maxLimit} locations.`}
          </Typography>
        )}
        {filteredOptions.length > 0 ? (
          filteredOptions.map(option => (
            <Box
              key={option.value}
              onClick={() => handleSelection(option.value)}
              sx={{
                padding: '10px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                transition: 'background 0.2s',
                // '&:hover': {backgroundColor: '#f5f5f5'},
              }}
            >
              <Checkbox
                checked={selectedValues.includes(String(option.value))}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: selectedValues.includes(String(option.value))
                    ? '400'
                    : '400',
                }}
              >
                {option.label}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              color: '#7E7E7E',
              fontSize: '12px',
              fontWeight: 400,
              opacity: 1,
              mt: 1,
            }}
          >
            No results found
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default MobileSelectDialogV2;
